import { AuthSession } from '@aws-amplify/core/dist/esm/singleton/Auth/types';
import { Link as AmplifyLink, Flex, Image, withAuthenticator, WithAuthenticatorProps } from "@aws-amplify/ui-react";
import { FetchUserAttributesOutput, GetCurrentUserOutput, signUp, SignUpInput } from 'aws-amplify/auth';
import type { Edulead } from 'edulead';
import React, { Component } from 'react';
import Modal from 'react-modal';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { Slide, ToastContainer } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { SideNav } from './custom-components/SideNav';
import { CustomEnvironment, getEnvironment, getFlavor } from './environment';
import { GlobalContext } from './global-context';
import { ApiPage } from './pages/API';
import { Billing } from './pages/Billing';
import { BuyingGroups } from './pages/BuyingGroups';
import {
  ExportStepExport,
  ExportStepFields,
  ExportStepOptions,
  ExportStepReview,
  ExportStepTarget
} from './pages/export-steps';
import { ExportDetail } from './pages/ExportDetail';
import { ExportsPage } from './pages/Exports';
import { Feed } from './pages/Feed';
import { Stream } from './pages/Stream';
import { MatchRunCreate } from './pages/MatchRunCreate';
import { MatchRunDetail } from './pages/MatchRunDetail';
import { MatchRuns } from './pages/MatchRuns';
import { InstitutionChildren } from './pages/InstitutionChildren';
import { InstitutionContacts } from './pages/InstitutionContacts';
import { InstitutionDetail } from './pages/InstitutionDetail';
import { Institutions } from './pages/Institutions';
import { IntegrationHubspot } from './pages/IntegrationHubspot';
import { IntegrationSalesforce } from './pages/IntegrationSalesforce';
import { Navigator } from './pages/Navigator';
import { NotFound } from './pages/NotFound';
import { People } from './pages/People';
import { Person } from './pages/Person';
import { Profile } from './pages/Profile';
import { Support } from './pages/Support';
import { ServiceLimits } from './pages/ServiceLimits';
import { TeamPage } from './pages/Team';
import { Tracking } from './pages/Tracking';
import { Trackings } from './pages/Trackings';
import { UnlockedInstitutions } from './pages/UnlockedInstitutions';
import { authService } from './services/auth.service';
import './styles/App.scss';
import { Entity } from './utils/CustomTypes';
import General from './utils/GeneralUtils';

export interface AuthInfo {
  teamId: string;
  userId: string;
  currentSession: AuthSession;
  currentAuthenticatedUser: GetCurrentUserOutput;
  userAttributes: FetchUserAttributesOutput;
  team: Edulead.EduleadV4Team | null;
  permissions: string[];
  role: string;
}

interface AppState {
  authInfo: AuthInfo | null;
  environment: CustomEnvironment;
  team: any;
  theme: 'light' | 'dark';
}

Modal.setAppElement('#root');

const RedirectToInstitutionContacts = () => {
  const { id } = useParams();
  return <Navigate to={`/directory/institutions/${id}/contacts`} replace />;
};

const RedirectLegacyInstitutionRoute = () => {
  const { id, '*': rest } = useParams();
  const path = rest ? `/directory/institutions/${id}/${rest}` : `/directory/institutions/${id}`;
  return <Navigate to={path} replace />;
};

const ActivityExportRedirect = () => {
  const { exportId } = useParams();
  return <Navigate to={`/exports/${exportId}`} replace />;
};

const PeopleWithDefaults = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  if (queryParams.has('q')) {
    return <People />;
  }
  const globalContext = React.useContext(GlobalContext);
  const defaultQueryString = globalContext.environment.entities.contact.defaultQueryString;
  return <Navigate to={`/directory/people${defaultQueryString}`} replace />;
};

// The Navigator moved from /sales/navigator (2026-11-01); old links keep their view and query.
const SalesNavigatorRedirect = () => {
  const { view } = useParams();
  const location = useLocation();
  return <Navigate to={`/navigator/search/${view ?? 'map'}${location.search}`} replace />;
};

// Beacons and territories are managed in the Navigator since 2026-11-03; a Settings link to one
// beacon opens it there.
const SettingsBeaconRedirect = () => {
  const { beaconId } = useParams();
  return <Navigate to={`/navigator/beacons/map?beacon=${beaconId}&edit=1`} replace />;
};

const PersonRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/directory/people/${id}`} replace />;
};

const InstitutionsWithDefaults = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  if (queryParams.has('q')) {
    return <Institutions />;
  }
  const globalContext = React.useContext(GlobalContext);
  const defaultQueryString = globalContext.environment.entities.institution.defaultQueryString;
  return <Navigate to={`/directory/institutions${defaultQueryString}`} replace />;
};

export class App extends Component<WithAuthenticatorProps, AppState> {
  props: WithAuthenticatorProps;
  constructor(props: WithAuthenticatorProps) {
    super(props)
    this.props = props;
    this.state = {
      authInfo: null,
      environment: getEnvironment(),
      team: null,
      theme: (localStorage.getItem('theme') as 'light' | 'dark') || 'light',
    }
  }

  async componentDidMount() {
    // Apply initial theme from state/localStorage
    document.documentElement.setAttribute('data-theme', this.state.theme);

    const authInfo = await authService.fetchAuthInfo()
    this.onSetState(authInfo)
  }

  onSetState(authInfo: AuthInfo) {
    this.setState({ authInfo })
  }

  setTheme(theme: 'light' | 'dark') {
    this.setState({ theme });
    localStorage.setItem('theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
  }

  render() {
    return (
      <GlobalContext.Provider value={{
        withAuthenticatorProps: this.props,
        authInfo: this.state.authInfo,
        environment: this.state.environment,
        onSetState: this.onSetState.bind(this),
        theme: this.state.theme,
        setTheme: this.setTheme.bind(this)
      }}>
        <BrowserRouter>
          <div className="app">
            <SideNav />
            <div className="right-side">
              <Routes>
                <Route path="/" element={<Navigate to={`/directory/people${this.state.environment.entities.contact.defaultQueryString}`} />} />
                <Route path="/directory" element={<Navigate to={`/directory/people${this.state.environment.entities.contact.defaultQueryString}`} />} />

                {/* Redirects for old /directory/contacts routes */}
                <Route path="/directory/contacts" element={<Navigate to="/directory/people" replace />} />
                <Route path="/directory/contacts/:id" element={<PersonRedirect />} />

                <Route path="/directory/people" element={<PeopleWithDefaults />} />
                <Route path="/directory/people/stream/:savedSearchId" element={<Stream />} />
                <Route path="/directory/people/:id" element={<Person />} />

                <Route path="/navigator" element={<Navigate to="/navigator/search/map" replace />} />
                <Route path="/navigator/:mode" element={<Navigator />} />
                <Route path="/navigator/:mode/:view" element={<Navigator />} />
                <Route path="/sales/navigator" element={<SalesNavigatorRedirect />} />
                <Route path="/sales/navigator/:view" element={<SalesNavigatorRedirect />} />

                <Route path="/directory/institutions" element={<InstitutionsWithDefaults />} />
                <Route path="/directory/institutions/:id" element={<RedirectToInstitutionContacts />} />
                <Route path="/directory/institutions/:id" element={<InstitutionDetail />}>
                  <Route path="schools" element={<InstitutionChildren entityType={Entity.SCHOOL} />} />
                  <Route path="contacts" element={<InstitutionContacts />} />
                  <Route path="*" element={<NotFound />} />
                </Route>

                {/* Legacy routes - redirect to institutions */}
                <Route path="/directory/districts" element={<Navigate to="/directory/institutions" replace />} />
                <Route path="/directory/districts/:id/*" element={<RedirectLegacyInstitutionRoute />} />
                <Route path="/directory/schools" element={<Navigate to="/directory/institutions" replace />} />
                <Route path="/directory/schools/:id/*" element={<RedirectLegacyInstitutionRoute />} />
                <Route path="/directory/agencies" element={<Navigate to="/directory/institutions" replace />} />
                <Route path="/directory/agencies/:id/*" element={<RedirectLegacyInstitutionRoute />} />

                <Route path="/tracking" element={<Trackings />} />
                <Route path="/tracking/:id" element={<Tracking />} />

                <Route path="/feed" element={<Feed />} />
                <Route path="/buying-groups" element={<BuyingGroups />} />

                <Route path="/api" element={<ApiPage />} />
                {/* <Route path="/fulltext" element={<Fulltext />} /> */}
                <Route path="/exports" element={<ExportsPage />} />
                <Route path="/exports/:exportId" element={<ExportDetail />}>
                  <Route path="target" element={<ExportStepTarget />} />
                  <Route path="fields" element={<ExportStepFields />} />
                  <Route path="options" element={<ExportStepOptions />} />
                  <Route path="review" element={<ExportStepReview />} />
                  <Route path="export" element={<ExportStepExport />} />
                </Route>
                {/* Redirects from old activity/exports routes */}
                <Route path="/activity" element={<Navigate to="/exports" replace />} />
                <Route path="/activity/exports" element={<Navigate to="/exports" replace />} />
                <Route path="/activity/exports/:exportId" element={<ActivityExportRedirect />} />
                <Route path="/team" element={<Navigate to="/settings/team" replace />} />
                <Route path="/settings/team" element={<TeamPage />} />
                <Route path="/tools/hubspot-companies" element={<MatchRuns />} />
                <Route path="/tools/hubspot-companies/new" element={<MatchRunCreate />} />
                <Route path="/tools/hubspot-companies/:id" element={<MatchRunDetail />} />
                <Route path="/tools/hubspot-companies/:id/:tab" element={<MatchRunDetail />} />
                {/* The enrichment flow was never linked from the nav, but internal links exist. */}
                <Route path="/tools/hubspot-company-enrichment/*" element={<Navigate to="/tools/hubspot-companies" replace />} />
                <Route path="/settings" element={<Navigate to="/settings/team" replace />} />
                <Route path="/settings/billing" element={<Billing />} />
                <Route path="/settings/integrations" element={<Navigate to="/settings/integrations/hubspot" replace />} />
                <Route path="/settings/integrations/hubspot" element={<IntegrationHubspot />} />
                <Route path="/settings/integrations/hubspot/callback" element={<IntegrationHubspot />} />
                <Route path="/settings/integrations/salesforce" element={<IntegrationSalesforce />} />
                <Route path="/settings/integrations/salesforce/callback" element={<IntegrationSalesforce />} />
                {/* Legacy callback routes - render directly to preserve query params */}
                <Route path="/settings/hubspot/callback" element={<IntegrationHubspot />} />
                <Route path="/settings/salesforce/callback" element={<IntegrationSalesforce />} />
                <Route path="/settings/service-limits" element={<ServiceLimits />} />
                <Route path="/settings/service-limits/unlocked-institutions" element={<UnlockedInstitutions />} />
                {/* Managed in the Navigator since 2026-11-03 */}
                <Route path="/settings/territories" element={<Navigate to="/navigator/territories/map" replace />} />
                <Route path="/settings/beacons" element={<Navigate to="/navigator/beacons/map" replace />} />
                <Route path="/settings/beacons/new" element={<Navigate to="/navigator/beacons/map" replace />} />
                <Route path="/settings/beacons/:beaconId" element={<SettingsBeaconRedirect />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/support" element={<Support />} />

                <Route path="/404" element={<NotFound />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </div>
          </div>
        </BrowserRouter>
        <ToastContainer
          transition={Slide}
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        <div id='modalroot'></div>
        <Tooltip id="main-tooltip" />
      </GlobalContext.Provider>
    );
  }
}

export default withAuthenticator(App, {
  services: {
    handleSignUp: async (input: SignUpInput) => {
      return signUp({
        username: input.username,
        password: input.password,
        options: {
          ...input.options,
          userAttributes: {
            ...input.options?.userAttributes,
            zoneinfo: getFlavor()
          }
        }
      })
    }
  },
  formFields: {
    signUp: {
      email: {
        order: 1,
      },
      password: {
        order: 2,
      },
      confirm_password: {
        order: 3,
      },
      given_name: {
        placeholder: '',
        label: 'First Name',
        order: 4
      },
      family_name: {
        placeholder: '',
        label: 'Last Name',
        order: 5
      }
    }
  },
  components: {
    Header: () => {
      const environment = getEnvironment()
      const theme = localStorage.getItem('theme') || 'light'
      const logoFile = theme === 'dark' ? environment.logo.replace('.svg', '-dark.svg') : environment.logo
      return <Flex className="app-auth-header" justifyContent='center'>
        <Image src={`/${logoFile}`} width={environment.logoWidth * 1.25} alt={environment.name} />
      </Flex>
    },
    Footer: () => {
      const environment = getEnvironment()
      return (
        <div className="app-auth-footer">
          <div className="app-auth-footer__copyright">
            © {General.getYear()} Edulead, Inc. All Rights Reserved.
          </div>
          <div className="app-auth-footer__links">
            <AmplifyLink
              target='_blank'
              href={environment.urlPrivacy}
              className="app-auth-footer__link"
            >
              Privacy Policy
            </AmplifyLink>
            <AmplifyLink
              target='_blank'
              href={environment.urlTerms}
              className="app-auth-footer__link"
            >
              Terms of Service
            </AmplifyLink>
            <AmplifyLink
              target='_blank'
              href={environment.urlStatus}
              className="app-auth-footer__link"
            >
              Status
            </AmplifyLink>
          </div>
        </div>
      );
    }
  }
});
